import React from 'react'
import _ from 'lodash'
import { State } from '.'
import { BaseAction } from './index'
import { EditPerformanceCell } from 'components/Shared/MuiTable/Cell/EditPerformanceCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { getStepDataHeaderCell } from 'components/Shared/MuiTable/Cell/StepDataHeaderCell'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { TASK_TYPE_ENUM, STEP_TYPE_ENUM, PLATFORM_CONTENT_TYPE_ENUM } from 'services/api/constants'
import { TimeWindowState } from './banners'
import { createSelector } from 'reselect'
import { ScheduleType } from './registrations'
import i18n from 'config/i18n'
import { snakecaseToTitleCase } from 'components/utils'

interface ExtraPayoutState {
    [key: string]: {
        uuid: string
        min: number
        max: number
        cost: number
        title: string
        description: string
    }
}

export enum EngagementId {
    VIEW = 'view',
    LIKE = 'like',
    REACH = 'reach',
    IMPRESSION = 'impression',
    COMMENT = 'comment',
    TAG = 'tag',
    SHARE = 'share',
    CONTENT_ENGAGEMENT_RATE = 'content_engagement_rate',
    TOTAL_ENGAGEMENT = 'total_engagement',
    // IG Story only
    STICKER_TAP = 'sticker_tap',
    LINK_CLICK = 'link_click',
    // tiktok only
    CTA_CLICK = 'cta_click',
    // tiktok and ig post only
    SAVE = 'save',
    // Twitter only
    REPLY = 'reply',
    RETWEET = 'retweet',
    ENGAGEMENT = 'engagement',
    ENGAGEMENT_RATE = 'engagement_rate',
    // IG Reels only
    TOTAL_WATCH_TIME = 'total_watch_time_second',
    // Tiktok only
    TOTAL_PLAY_TIME = 'total_play_time_second',
    WATCHED_FULL_VIDEO = 'watched_full_video_percentage',
    // IG Reels and Tiktok only
    AVERAGE_WATCH_TIME = 'average_watch_time_second',
}

export const EngagementIds = Object.values(EngagementId)

export enum NoneEngagementPayoutType {
    BASE = 'base',
    EXTRAS = 'extras',
}

export interface PayoutState {
    base?: {
        cost: number
        min?: number
        max?: number
        uuid?: string
    }
    view?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    like?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    sticker_tap?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    reach?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    impression?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    comment?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    share?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    tag?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    save?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    cta_click?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    reply?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    retweet?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    engagement?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    link_click?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    total_watch_time?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    total_play_time?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    watched_full_video?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    average_watch_time?: {
        cost: number
        min: number
        max: number
        uuid: string
    }
    extras?: ExtraPayoutState
}

export interface StepState {
    uuid: string
    type: STEP_TYPE_ENUM
    time_window: {
        start: string
        end: string
    }
    data: Record<string, any> // depends on step type
    is_deadline_extendable: boolean
    extended_deadline: string
    is_caption_reviews_done: boolean
}

export enum BLOCK_TYPE {
    TEXT = 'text',
    PHOTO_CAROUSEL = 'photo_carousel',
    PHOTO_SUBMISSION = 'photo_submission',
    TEXT_SUBMISSION = 'text_submission',
    URL_SUBMISSION = 'url_submission',
}

interface CustomStepTextBlock {
    type: BLOCK_TYPE.TEXT
    title: string
    description: string
    uuid: string
}
interface CustomStepPhotoBlock {
    type: BLOCK_TYPE.PHOTO_CAROUSEL
    title: string
    description: string
    files: Array<any>
    allow_download: boolean
    uuid: string
}
interface CustomStepPhotoSubmissionBlock {
    type: BLOCK_TYPE.PHOTO_SUBMISSION
    title: string
    max_photos: number
    uuid: string
}
interface CustomStepTextSubmissionBlock {
    type: BLOCK_TYPE.TEXT_SUBMISSION
    title: string
    uuid: string
}
interface CustomStepUrlSubmissionBlock {
    type: BLOCK_TYPE.URL_SUBMISSION
    title: string
    uuid: string
}

export type CustomStepBlock =
    | CustomStepTextBlock
    | CustomStepPhotoBlock
    | CustomStepPhotoSubmissionBlock
    | CustomStepTextSubmissionBlock
    | CustomStepUrlSubmissionBlock

export enum LINK_IN_BIO_TYPE_ENUM {
    NO_LINK = 0,
    PREDEFINED_LINK = 1,
    DYNAMIC_LINK = 2,
}

export interface TaskState {
    uuid: string
    type: TASK_TYPE_ENUM
    platform: number
    custom_platform: boolean | null
    title: string
    description: string
    payouts: PayoutState
    platform_content_type: PLATFORM_CONTENT_TYPE_ENUM | null
    platform_content_type_others: string | null
    steps: StepState[]
    data?: any
    post_time_window?: {
        start: string
        end: string
    }
    add_post_time?: boolean
    ordering_index: number
}

type TasksEntitiesState = {
    [uuid: string]: TaskState
}

export type TasksState = {
    tasks: string[]
    byId: TasksEntitiesState
    editing: {
        tasks: string[]
        byId: TasksEntitiesState
    }
}

const initialState: TasksState = {
    tasks: [],
    byId: {},
    editing: {
        tasks: [],
        byId: {},
    },
}

export enum TasksActions {
    TASKS_LIST_REQUEST = 'tasks/TASKS_LIST_REQUEST',
    SAVE_TASKS_LIST = 'tasks/SAVE_TASKS_LIST',
    CLEAR_TASKS_LIST = 'tasks/CLEAR_TASKS_LIST',
    REGISTER_TASK_REQUEST = 'tasks/REGISTER_TASK_REQUEST',
    SAVE_REGISTERED_TASK = 'tasks/SAVE_REGISTERED_TASK',
    UPDATE_TASK_REQUEST = 'tasks/UPDATE_TASK_REQUEST',
    DELETE_TASK_REQUEST = 'tasks/DELETE_TASK_REQUEST',
    REMOVE_FROM_TASKS_LIST = 'tasks/REMOVE_FROM_TASKS_LIST',
    UPDATE_TASK_PAYOUTS_REQUEST = 'tasks/UPDATE_TASK_PAYOUTS_REQUEST',
    UPDATE_TASK_SCHEDULES_REQUEST = 'tasks/UPDATE_TASK_SCHEDULES_REQUEST',
    UPDATE_TASK_ORDER = 'tasks/UPDATE_TASK_UPDATE_TASK_ORDER',
}

export const BLOCK_TYPES_WITH_PARTIPOSTER_CONTENT = [
    BLOCK_TYPE.PHOTO_SUBMISSION,
    BLOCK_TYPE.TEXT_SUBMISSION,
    BLOCK_TYPE.URL_SUBMISSION,
]
export interface TasksListRequestPayload {
    isMockSubmissionProof?: boolean
    campaignUuid: string
    verifyCaptionReviewTaskUuid?: string
    captionReviewCallback?: (isCaptionReviewDone: boolean) => void
}

export const getTasksList = (payload: TasksListRequestPayload) => ({
    payload,
    type: TasksActions.TASKS_LIST_REQUEST,
})

export const saveTasksList = (payload: { data?: TaskState[]; isMockSubmissionProof?: boolean }) => ({
    payload,
    type: TasksActions.SAVE_TASKS_LIST,
})

export const clearTasksList = () => ({
    type: TasksActions.CLEAR_TASKS_LIST,
})

export interface RegisterTaskRequestPayload {
    taskType: string
    campaignUuid: string
}

export const registerTask = (payload: RegisterTaskRequestPayload) => ({
    payload,
    type: TasksActions.REGISTER_TASK_REQUEST,
})

export const saveRegisteredTask = (payload: { data?: TaskState[] }) => ({
    payload,
    type: TasksActions.SAVE_REGISTERED_TASK,
})

export interface UpdateTaskRequestPayload {
    task: TaskState
    campaignUuid: string
    idle_hours_to_submit_engagement?: number
    onSuccessCallback?: () => void
}

export const updateTask = (payload: UpdateTaskRequestPayload) => ({
    payload,
    type: TasksActions.UPDATE_TASK_REQUEST,
})

export interface DeleteTaskRequestPayload {
    taskUuid: string
    campaignUuid: string
}

export const deleteTask = (payload: DeleteTaskRequestPayload) => ({
    payload,
    type: TasksActions.DELETE_TASK_REQUEST,
})

export const removeFromTasksList = (payload: { taskUuid?: string }) => ({
    payload,
    type: TasksActions.REMOVE_FROM_TASKS_LIST,
})

export interface UpdateTaskPayoutsRequestPayload {
    taskUuid: string
    payouts: PayoutState
}

export const updateTaskPayouts = (payload: UpdateTaskPayoutsRequestPayload) => ({
    payload,
    type: TasksActions.UPDATE_TASK_PAYOUTS_REQUEST,
})

export interface UpdateStepTimeWindow {
    uuid: string
    time_window: TimeWindowState
}

export interface UpdateTaskScheduleRequestPayload {
    taskUuid: string
    steps: UpdateStepTimeWindow[]
    data?: { add_post_time: boolean }
}

export const updateTaskSchedules = (payload: UpdateTaskScheduleRequestPayload) => ({
    payload,
    type: TasksActions.UPDATE_TASK_SCHEDULES_REQUEST,
})

export interface UpdateTasksOrderingRequestPayload {
    ordering: string[]
    campaignUuid: string
}

export const updateTaskOrder = (payload: UpdateTasksOrderingRequestPayload) => ({
    payload,
    type: TasksActions.UPDATE_TASK_ORDER,
})

export const tasksReducer = (state: TasksState = initialState, action: BaseAction) => {
    switch (action.type) {
        case TasksActions.SAVE_TASKS_LIST: {
            const { data, isMockSubmissionProof } = action.payload
            const returnData = _.cloneDeep(data).map((task: TaskState | any) => {
                // isMockSubmissionProof only be true for the related task API used in the editor.
                // This can prevent the Mock task from affecting the display on the review page.
                // Mock submit proof photo block for display only , reason : backend won't response the block if is toggle off
                if (
                    isMockSubmissionProof &&
                    [TASK_TYPE_ENUM.ATTEND_EVENT, TASK_TYPE_ENUM.PURCHASE_PRODUCT].includes(task.type)
                ) {
                    if (!task.steps.find((step: StepState) => step.type === STEP_TYPE_ENUM.SUBMIT_PROOF_PHOTO)) {
                        task.steps.push({
                            uuid: '__mock__',
                            type: STEP_TYPE_ENUM.SUBMIT_PROOF_PHOTO,
                            time_window: null,
                            data: {
                                title: i18n.t('placeholderText.enterStepTitleSubmitProofPhoto'),
                            },
                        })
                    }
                }
                task.steps.forEach((step: StepState) => {
                    Object.keys(step.data).forEach((dataType: string) => {
                        const isStringArrayValue =
                            Array.isArray(step.data[dataType]) &&
                            step.data[dataType].every((item: any) => typeof item === 'string')
                        if (isStringArrayValue) {
                            const migrateValue = step.data[dataType].map((value: string) => {
                                return {
                                    uuid: '',
                                    value: value,
                                }
                            })
                            step.data[dataType] = migrateValue
                        }
                    })
                })
                return task
            })
            const uuids = returnData
                .sort((pre: TaskState, next: TaskState) => pre?.ordering_index - next?.ordering_index)
                .map((task: TaskState) => task.uuid)
            let updatedById = returnData.reduce(
                (memo: TasksEntitiesState, task: TaskState) => Object.assign({}, memo, { [task.uuid]: task }),
                state.byId,
            )
            if (returnData.length == 1) {
                // partial update, need to sync link in bio returnData to all IG task
                const toCopy = returnData[0]
                for (const i in state.byId) {
                    const byId = state.byId[i]
                    if (
                        byId.uuid != toCopy.uuid &&
                        toCopy.type.includes('instagram_') &&
                        byId.type.includes('instagram_')
                    ) {
                        if (toCopy) {
                            byId.data.link_in_bio_type = toCopy.data.link_in_bio_type
                            byId.data.link_in_bio_url = toCopy.data.link_in_bio_url
                            byId.data.link_in_bio_duration = toCopy.data.link_in_bio_duration
                        }
                        updatedById = Object.assign({}, updatedById, { [byId.uuid]: byId })
                    }
                }
            }

            return Object.assign({}, state, {
                tasks: _.uniq([...state.tasks, ...uuids]),
                byId: updatedById,
            })
        }
        case TasksActions.SAVE_REGISTERED_TASK: {
            const { data } = action.payload
            const uuids = data.map((task: TaskState) => task.uuid)
            return Object.assign({}, state, {
                editing: {
                    tasks: _.uniq([...state.editing.tasks, ...uuids]),
                    byId: data.reduce(
                        (memo: TasksEntitiesState, task: TaskState) => Object.assign({}, memo, { [task.uuid]: task }),
                        state.editing.byId,
                    ),
                },
            })
        }
        case TasksActions.CLEAR_TASKS_LIST: {
            return Object.assign({}, state, {
                tasks: [],
                byId: {},
            })
        }
        case TasksActions.REMOVE_FROM_TASKS_LIST: {
            const { taskUuid } = action.payload
            const taskUuids = state.tasks.filter((uuid: string) => uuid !== taskUuid)
            return Object.assign({}, state, {
                tasks: taskUuids,
            })
        }
        case TasksActions.UPDATE_TASK_ORDER: {
            const { ordering } = action.payload
            ordering.forEach((uuid: string, index: number) => {
                if (state.byId.hasOwnProperty(uuid)) {
                    state.byId[uuid].ordering_index = index + 1
                }
            })
            return Object.assign({}, state, {
                tasks: _.uniq([...ordering]),
            })
        }
        default:
            return state
    }
}

export const selectTasksState = (state: State) => state.tasks
export const selectTaskIds = (state: State) => selectTasksState(state).tasks
export const selectTasks = createSelector(selectTasksState, tasksState =>
    tasksState.tasks.map(uuid => tasksState.byId[uuid]),
)
export const selectAllSteps = createSelector(selectTasks, tasks => tasks.map(task => task.steps).flat(1))
export const selectTaskByUuid =
    (uuid: string) =>
    (state: State): TaskState =>
        selectTasksState(state).byId[uuid]
export const selectSubmitContentTaskUuids = (state: State): string[] => selectTasks(state).map(task => task.uuid)
export const selectSubmitContentStepUuids = (state: State) =>
    selectTasks(state)
        .map(task =>
            task.steps
                .filter(
                    step =>
                        step.type.includes('submit_content') ||
                        step.type.includes('custom') ||
                        step.type.includes('submit_proof_photo'),
                )
                .map(step => step.uuid),
        )
        .flat()
export const selectStepUuidsByTaskUuid =
    (taskUuid: string) =>
    (state: State): string[] =>
        selectTaskByUuid(taskUuid)(state).steps.map(step => step.uuid)
export const selectTasksPayoutColumns =
    (hide_payout_info: boolean, currencySign: string) =>
    (state: State): GridColDef[] =>
        selectTasks(state)
            .map(task => {
                const { extras, ...standardPayout } = task.payouts
                const payout = {
                    ...standardPayout,
                    ...extras,
                } as any

                const HEADER_NAME_SPLITTER = ' - '
                const perfArr = Object.keys(payout).map(performanceKey => {
                    const performanceData = payout[performanceKey]
                    let title
                    if (performanceData.hasOwnProperty('title')) {
                        title = performanceData.title
                    } else {
                        title = snakecaseToTitleCase(performanceKey)
                    }
                    let details = performanceData.max
                        ? `(${performanceData.min} - ${performanceData.max} @ ${currencySign} ${performanceData.cost})`
                        : `(${currencySign} ${performanceData.cost})`
                    details = hide_payout_info ? '' : details
                    return {
                        field: task.uuid + '_' + performanceKey,
                        headerName: `${task.title}${HEADER_NAME_SPLITTER}${title} ${details}`,
                        width: 200,
                        renderHeader: getStepDataHeaderCell(HEADER_NAME_SPLITTER),
                        renderCell: (props: GridRenderCellParams) =>
                            performanceKey === NoneEngagementPayoutType.BASE ? (
                                <StringCell {...props} />
                            ) : (
                                <EditPerformanceCell {...props} />
                            ),
                        sortable: false,
                        filterable: false,
                    }
                })
                return perfArr
            })
            .flat()

export const selectTasksPlatformTypes = createSelector(selectTasks, tasks => {
    const platforms = tasks.map(task => task.platform).sort()
    return [...new Set(platforms)]
})

export const selectTasksEarliestStartAndEndDate = createSelector([selectTasks], (tasks): ScheduleType => {
    let earliestStartDate: string | null = null
    let earliestEndDate: string | null = null

    tasks.forEach(task => {
        if (task.steps && task.steps.length > 0) {
            task.steps.forEach(step => {
                if (step.time_window) {
                    const startDate = step.time_window?.start
                    const endDate = step.time_window?.end

                    if (startDate) {
                        if (
                            !earliestStartDate ||
                            (earliestStartDate && new Date(startDate) < new Date(earliestStartDate))
                        ) {
                            earliestStartDate = startDate
                        }
                    }

                    if (endDate) {
                        if (!earliestEndDate || (earliestEndDate && new Date(endDate) < new Date(earliestEndDate))) {
                            earliestEndDate = endDate
                        }
                    }
                }
            })
        }
    })
    return {
        start: earliestStartDate,
        end: earliestEndDate,
    }
})
